import React from 'react';
import {useTranslation} from 'react-i18next';
import {fetcher, safeCaptureException} from '../utils';
import {
  DOCUMENTS_TYPES,
  getRemappedOfficialDocType,
  IMAGE_SERVICE_ORIGIN,
  IV_PHOTO_SOURCE,
  OCR_CHECKING_STATUSES,
  OCRDetectedData,
} from '@chekin/core';
import {API} from '@chekin/api';
import {ANALYTIC_EVENTS, Analytics} from '../analytics';
import {getOCRFailure, type OCRFailure} from './iv/ocrFailure';

const MaxRetriesNumber = 40;
const RetryTimeoutMs = 1000;

const isBarcodeDetection = (documentType = '') => {
  return [
    DOCUMENTS_TYPES.drivingLicenseUS,
    DOCUMENTS_TYPES.drivingLicenseCA,
    DOCUMENTS_TYPES.identityCardCO,
  ].includes(documentType);
};

type IVCheckProps = {
  image: string;
  countryCode?: string;
  source: IV_PHOTO_SOURCE;
  reservationId?: string;
  documentType?: string;
  nationality?: string;
  intentId?: string;
};

type CheckOCRStatusProps = {
  image: string;
  processId: string;
  retries?: number;
  documentType?: string;
};

type checkOCRResponse =
  | {
      mrzDetected?: boolean;
      detectionFailed?: boolean;
    }
  | undefined;

type UseDocumentSelfieDetection = {
  onError?: (error: Error) => void;
  onFinish?: (image: string, data?: OCRDetectedData) => void;
  onBefore?: () => void;
  onDetectionFailed?: (
    failure: OCRFailure & {title: string; description: string},
    data: OCRDetectedData & {image: string},
  ) => void;
  onRetriesFinish?: (data: OCRDetectedData, retries: number) => void;
  intentId?: string;
};

function useOCR({
  onFinish,
  onBefore,
  onError,
  onDetectionFailed,
  onRetriesFinish,
  intentId: defaultIntentId,
}: UseDocumentSelfieDetection) {
  const {t} = useTranslation();
  const retryTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
  const isActiveRef = React.useRef(true);

  React.useEffect(() => {
    isActiveRef.current = true;
    return () => {
      isActiveRef.current = false;
      clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  const handleOCRStatus = React.useCallback(
    (image: string, data: OCRDetectedData) => {
      const mrzDetected = data?.mrz_detected;
      const barcodeDetected = data?.barcode_detected;

      if (!mrzDetected && !barcodeDetected) {
        const failure = getOCRFailure(data);
        onDetectionFailed?.(
          {
            ...failure,
            title: t(failure.titleKey),
            description: t(failure.descriptionKey),
          },
          {...data, image},
        );
      } else {
        onFinish?.(image, data);
      }
    },
    [t, onFinish, onDetectionFailed],
  );

  const checkOCRProcessStatus = React.useCallback(
    async ({
      image,
      processId,
      documentType,
      retries,
    }: CheckOCRStatusProps): Promise<checkOCRResponse> => {
      try {
        const data = await fetcher(
          API.identityVerification.checkImageOCRStatus,
          processId,
        );
        if (!isActiveRef.current) return;
        const isInProgress =
          data?.status === OCR_CHECKING_STATUSES.processing ||
          data?.status === OCR_CHECKING_STATUSES.new;

        if (isInProgress) {
          const newRetries = retries || 0;
          if (newRetries >= MaxRetriesNumber) {
            onRetriesFinish?.(data, newRetries);
            return;
          }

          retryTimeoutRef.current = setTimeout(async () => {
            await checkOCRProcessStatus({
              image,
              processId,
              retries: newRetries + 1,
              documentType,
            });
          }, RetryTimeoutMs);

          return;
        }

        if (!data) {
          return;
        }
        Analytics.track(ANALYTIC_EVENTS.ivGettingResultsFromOCR, {
          check_id: data.id,
          status: data.status,
          detected_document_type: data.type_doc ?? '',
          picture_file: data.picture_file,
          mrz_detected: Boolean(data.mrz_detected),
          barcode_detected: Boolean(data.barcode_detected),
          is_barcode_detection: isBarcodeDetection(documentType),
          type: IMAGE_SERVICE_ORIGIN.ocr,
          retries: retries ?? 0,
          selected_document_type: documentType,
        });
        handleOCRStatus(image, data);
      } catch (error) {
        if (!isActiveRef.current) return;
        safeCaptureException(error, 'OCR process status check failed');

        if (error && typeof error === 'object' && 'message' in error) {
          onError?.(error as Error);
        } else if (typeof error === 'string') {
          onError?.(new Error(error));
        } else {
          onError?.(new Error('OCR process failed due to an unknown error'));
        }
      }
    },
    [handleOCRStatus, onError, onRetriesFinish],
  );

  const sendAndVerifyImageForOCR = React.useCallback(
    async ({
      image,
      source,
      reservationId,
      nationality,
      documentType,
      countryCode,
      intentId,
    }: IVCheckProps) => {
      onBefore?.();

      try {
        const remappedOfficialDocType = getRemappedOfficialDocType({
          docType: documentType,
        });
        const data = await fetcher(API.identityVerification.sendImageForOCR, {
          picture_file: image,
          iv_intent_id: intentId ?? defaultIntentId,
          for_country: countryCode || undefined,
          reservation_id: reservationId,
          type_doc: remappedOfficialDocType,
          nationality,
          source,
        });
        if (!isActiveRef.current || !data?.id) return;

        Analytics.track(ANALYTIC_EVENTS.ivPhotoSent, {
          check_id: data.id,
          picture_file: data.picture_file,
          status: data.status,
          mrz_detected: data.mrz_detected,
          barcode_detected: Boolean(data.barcode_detected),
          is_barcode_detection: isBarcodeDetection(documentType),
          type: IMAGE_SERVICE_ORIGIN.ocr,
          selected_document_type: documentType,
        });

        await checkOCRProcessStatus({
          processId: data?.id,
          image,
        });
      } catch (error) {
        if (!isActiveRef.current) return;
        safeCaptureException(error, 'OCR image upload failed');

        if (error && typeof error === 'object' && 'message' in error) {
          onError?.(error as Error);
        } else if (typeof error === 'string') {
          onError?.(new Error(error));
        } else {
          onError?.(new Error('Image upload failed due to an unknown error'));
        }
      }
    },
    [onBefore, checkOCRProcessStatus, onError, defaultIntentId],
  );

  const sendToOCR = React.useCallback(
    ({
      image,
      source,
      reservationId,
      nationality,
      documentType,
      countryCode,
      intentId,
    }: IVCheckProps) => {
      const remappedOfficialDocType = getRemappedOfficialDocType({
        docType: documentType,
      });
      return API.identityVerification.sendImageForOCR({
        picture_file: image,
        iv_intent_id: intentId ?? defaultIntentId,
        for_country: countryCode || undefined,
        reservation_id: reservationId,
        type_doc: remappedOfficialDocType,
        nationality,
        source,
      });
    },
    [defaultIntentId],
  );

  const checkOCRResult = React.useCallback((sentImageId: string) => {
    return API.identityVerification.checkImageOCRStatus(sentImageId);
  }, []);

  return {
    sendAndVerifyImageForOCR,
    checkOCRResult,
    sendToOCR,
  };
}

export {useOCR};
